// Native-browser printing for Mid-Term and Final meeting packets.
// This deliberately follows the same model as syllabus printing:
// real HTML/text in an offscreen iframe, then the browser print dialog.

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function textHtml(value) {
  const escaped = escapeHtml(value);

  return escaped
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\n/g, '<br />');
}

function displayStudentName(student) {
  const english =
    String(student?.englishName || '').trim();

  const chinese =
    String(student?.chineseName || '').trim();

  if (english && chinese) {
    return `${english} (${chinese})`;
  }

  return (
    english ||
    chinese ||
    String(student?.studentCode || '').trim() ||
    'Student'
  );
}

function formatMeetingDate(value) {
  if (!value) return '—';

  const text =
    String(value).slice(0, 10);

  const match =
    text.match(
      /^(\d{4})-(\d{2})-(\d{2})$/
    );

  if (!match) {
    return String(value);
  }

  return `${match[1]}-${match[2]}-${match[3]}`;
}

function statCell(label, value) {
  return `
    <div class="stat-card">
      <div class="stat-label">${escapeHtml(label)}</div>
      <div class="stat-value">${escapeHtml(value)}</div>
    </div>
  `;
}

function renderSelfEvaluation(items) {
  const rows =
    Array.isArray(items)
      ? items
      : [];

  if (!rows.length) {
    return `
      <div class="empty">
        No submitted self-evaluation responses were available.
      </div>
    `;
  }

  return rows
    .map(item => `
      <div class="evaluation-item">
        <h3>${escapeHtml(item?.sectionTitle || 'Self-Evaluation')}</h3>

        <div class="response-block">
          <div class="response-label">Student response</div>
          <div class="response-text">
            ${textHtml(item?.answer || '—')}
          </div>
        </div>

        ${
          item?.justification
            ? `
              <div class="response-block">
                <div class="response-label">Justification</div>
                <div class="response-text">
                  ${textHtml(item.justification)}
                </div>
              </div>
            `
            : ''
        }

        ${
          item?.instructorComment
            ? `
              <div class="response-block instructor-comment">
                <div class="response-label">Instructor comment</div>
                <div class="response-text">
                  ${textHtml(item.instructorComment)}
                </div>
              </div>
            `
            : ''
        }
      </div>
    `)
    .join('');
}

function renderPdNotes(notes) {
  const rows =
    Array.isArray(notes)
      ? notes
      : [];

  if (!rows.length) {
    return `
      <div class="empty">
        No Philosophical Diary instructor notes.
      </div>
    `;
  }

  return `
    <div class="notes-list">
      ${rows
        .map(note => `
          <div class="note-row">
            <div class="note-heading">
              ${
                note?.weekNumber
                  ? `Week ${escapeHtml(note.weekNumber)}`
                  : 'Diary note'
              }
            </div>
            <div class="note-text">
              ${textHtml(note?.note || '')}
            </div>
          </div>
        `)
        .join('')}
    </div>
  `;
}

function renderAssignments(assignments) {
  const rows =
    Array.isArray(assignments)
      ? assignments
      : [];

  if (!rows.length) {
    return `
      <div class="empty">
        No other assignments were available.
      </div>
    `;
  }

  return `
    <table class="assignment-table">
      <thead>
        <tr>
          <th>Assignment</th>
          <th>Status</th>
          <th>Instructor note</th>
        </tr>
      </thead>
      <tbody>
        ${rows
          .map(item => `
            <tr>
              <td>${escapeHtml(item?.label || 'Assignment')}</td>
              <td>${escapeHtml(item?.status || '—')}</td>
              <td>${textHtml(item?.instructorNote || '—')}</td>
            </tr>
          `)
          .join('')}
      </tbody>
    </table>
  `;
}

function renderStudentPage({
  student,
  courseName,
  semester,
  meetingType,
  packetLabel
}) {
  const name =
    displayStudentName(student);

  const finalGrade =
    String(student?.finalGrade || '').trim();

  const photoId = String(student?.photoFileId || '').trim();

  const photoHtml = photoId
    ? `<img
        class="student-photo"
        src="https://drive.google.com/thumbnail?id=${encodeURIComponent(photoId)}&sz=w240"
        alt="Student photo"
        onerror="this.remove()"
      />`
    : '';

  const packetText =
    packetLabel
      ? `<div class="packet-label">${escapeHtml(packetLabel)}</div>`
      : '';

  return `
    <section class="student-report">
      <header class="report-header">
        <div>
          <div class="course-name">${escapeHtml(courseName)}</div>
          <div class="report-title">${escapeHtml(meetingType)} Meeting</div>
          <div class="semester">${escapeHtml(semester || '')}</div>
        </div>

        ${packetText}
      </header>

      <div class="student-heading">
        <div>
          <h1>${escapeHtml(name)}</h1>
          <div class="student-id">
            Student ID: ${escapeHtml(student?.studentCode || '—')}
          </div>
        </div>

        <div class="student-heading-right">
          <div class="meeting-meta">
            <div>
              <span>Date</span>
              ${escapeHtml(formatMeetingDate(student?.meetingDate))}
            </div>
            <div>
              <span>Time</span>
              ${escapeHtml(student?.meetingTime || '—')}
            </div>
          </div>
          ${photoHtml}
        </div>
      </div>

      <section class="report-section">
        <h2>Progress to Date</h2>

        <div class="stats-grid">
          ${statCell(
            'Attendance',
            `${student?.attPresent ?? 0}/${student?.attTotal ?? 0}`
          )}
          ${statCell(
            'Critical Questions',
            `${student?.cqCount ?? 0}/${student?.cqTotal ?? 0}`
          )}
          ${statCell(
            'Philosophical Diary',
            `${student?.pdCount ?? 0}/${student?.pdTotal ?? 0}`
          )}
          ${statCell(
            'Suggested Grade',
            student?.suggestedGrade || '—'
          )}
          ${
            finalGrade
              ? statCell(
                  'Final Grade',
                  finalGrade
                )
              : ''
          }
        </div>
      </section>

      <section class="report-section">
        <h2>Other Assignments</h2>
        ${renderAssignments(student?.otherAssignments)}
      </section>

      <section class="report-section">
        <h2>Self-Evaluation</h2>
        ${renderSelfEvaluation(student?.selfEvalItems)}
      </section>

      <section class="report-section">
        <h2>Philosophical Diary Notes</h2>
        ${renderPdNotes(student?.pdNotes)}
      </section>


    </section>
  `;
}

function waitForImages(doc) {
  return Promise.all(
    Array.from(doc.images || [])
      .map(image => {
        if (image.complete) {
          return Promise.resolve();
        }

        return new Promise(resolve => {
          const finish = () => resolve();

          image.addEventListener(
            'load',
            finish,
            { once: true }
          );

          image.addEventListener(
            'error',
            finish,
            { once: true }
          );
        });
      })
  );
}

export async function printMeetingPdf({
  courseName,
  semester,
  meetingType,
  students,
  packetLabel = '',
  fileName = ''
}) {
  const printableStudents =
    Array.isArray(students)
      ? students
      : [];

  if (!printableStudents.length) {
    throw new Error(
      'No students with meeting slots were available to print.'
    );
  }

  const title =
    String(
      fileName ||
      `${courseName || 'Course'} - ${meetingType || 'Meeting'}`
    )
      .replace(/\.pdf$/i, '');

  const bodyHtml =
    printableStudents
      .map(student =>
        renderStudentPage({
          student,
          courseName:
            courseName || 'Course',
          semester,
          meetingType:
            meetingType || 'Meeting',
          packetLabel
        })
      )
      .join('');

  const frame =
    document.createElement(
      'iframe'
    );

  frame.setAttribute(
    'aria-hidden',
    'true'
  );

  Object.assign(
    frame.style,
    {
      position: 'fixed',
      width: '182mm',
      height: '297mm',
      border: '0',
      left: '-10000px',
      top: '0',
      pointerEvents: 'none'
    }
  );

  document.body.appendChild(
    frame
  );

  const printDocument =
    frame.contentDocument;

  const printWindow =
    frame.contentWindow;

  if (
    !printDocument ||
    !printWindow
  ) {
    frame.remove();

    throw new Error(
      'The browser could not create the meeting print document.'
    );
  }

  printDocument.open();

  printDocument.write(`
<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>

  <style>
    @page {
      size: A4 portrait;
      margin: 13mm 14mm 15mm;
    }

    * {
      box-sizing: border-box;
    }

    html,
    body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #1f2933;
      font-family:
        Arial,
        "Noto Sans TC",
        "Microsoft JhengHei",
        sans-serif;
      font-size: 10.5pt;
      line-height: 1.42;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .student-report {
      width: 100%;
      break-after: page;
      page-break-after: always;
    }

    .student-report:last-child {
      break-after: auto;
      page-break-after: auto;
    }

    .report-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 18px;
      border-bottom: 2px solid #233746;
      padding-bottom: 8px;
      margin-bottom: 14px;
    }

    .course-name {
      font-size: 15pt;
      font-weight: 700;
      line-height: 1.2;
    }

    .report-title {
      margin-top: 3px;
      font-size: 12pt;
      font-weight: 700;
    }

    .semester {
      margin-top: 2px;
      color: #526777;
      font-size: 9.5pt;
    }

    .packet-label {
      border: 1px solid #b8c5ce;
      border-radius: 999px;
      padding: 4px 9px;
      font-size: 9pt;
      font-weight: 700;
      color: #334e68;
      white-space: nowrap;
    }

    .student-heading {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 18px;
      margin-bottom: 16px;
      break-inside: avoid-page;
      page-break-inside: avoid;
    }

    h1 {
      margin: 0;
      font-size: 18pt;
      line-height: 1.18;
    }

    .student-id {
      margin-top: 4px;
      color: #526777;
      font-size: 9.5pt;
    }

    .student-heading-right {
      display: flex;
      align-items: flex-start;
      gap: 10mm;
      flex-shrink: 0;
    }

    .student-photo {
      width: 25mm;
      height: 32mm;
      object-fit: cover;
      object-position: center top;
      border: 1px solid #d7dee5;
      border-radius: 4px;
      display: block;
    }

    .meeting-meta {
      min-width: 128px;
      font-size: 9.5pt;
    }

    .meeting-meta div {
      margin-bottom: 3px;
    }

    .meeting-meta span {
      display: inline-block;
      width: 38px;
      color: #66788a;
      font-weight: 700;
    }

    .report-section {
      margin-top: 16px;
    }

    .report-section > h2 {
      margin: 0 0 8px;
      padding-bottom: 4px;
      border-bottom: 1px solid #cfd8df;
      font-size: 12pt;
      line-height: 1.2;
      break-after: avoid-page;
      page-break-after: avoid;
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 7px;
      break-inside: avoid-page;
      page-break-inside: avoid;
    }

    .stat-card {
      border: 1px solid #d7dee5;
      border-radius: 6px;
      padding: 8px 9px;
      min-height: 49px;
    }

    .stat-label {
      color: #66788a;
      font-size: 8.5pt;
      font-weight: 700;
    }

    .stat-value {
      margin-top: 2px;
      font-size: 12pt;
      font-weight: 700;
    }

    .evaluation-item {
      border: 1px solid #d7dee5;
      border-radius: 7px;
      padding: 10px 11px;
      margin-bottom: 8px;
    }

    .evaluation-item h3 {
      margin: 0 0 7px;
      font-size: 10.5pt;
      break-after: avoid-page;
      page-break-after: avoid;
    }

    .response-block {
      margin-top: 7px;
    }

    .response-label {
      margin-bottom: 2px;
      color: #66788a;
      font-size: 8.5pt;
      font-weight: 700;
    }

    .response-text,
    .note-text,
    td {
      overflow-wrap: anywhere;
      word-break: normal;
      white-space: normal;
      widows: 3;
      orphans: 3;
    }

    .instructor-comment {
      border-left: 3px solid #9fb9ca;
      padding-left: 8px;
    }

    .notes-list {
      border: 1px solid #d7dee5;
      border-radius: 7px;
      overflow: hidden;
    }

    .note-row {
      padding: 8px 10px;
      border-bottom: 1px solid #e4e9ed;
      break-inside: avoid-page;
      page-break-inside: avoid;
    }

    .note-row:last-child {
      border-bottom: 0;
    }

    .note-heading {
      margin-bottom: 2px;
      font-size: 8.5pt;
      font-weight: 700;
      color: #526777;
    }

    .assignment-table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      font-size: 9pt;
    }

    .assignment-table thead {
      display: table-header-group;
    }

    .assignment-table tr {
      break-inside: avoid-page;
      page-break-inside: avoid;
    }

    .assignment-table th,
    .assignment-table td {
      border: 1px solid #d7dee5;
      padding: 6px 7px;
      text-align: left;
      vertical-align: top;
    }

    .assignment-table th {
      background: #f1f4f6;
      font-weight: 700;
    }

    .assignment-table th:nth-child(1),
    .assignment-table td:nth-child(1) {
      width: 29%;
    }

    .assignment-table th:nth-child(2),
    .assignment-table td:nth-child(2) {
      width: 18%;
    }

    .empty {
      color: #7b8794;
      font-style: italic;
      font-size: 9pt;
    }
  </style>
</head>

<body>
  ${bodyHtml}
</body>
</html>
  `);

  printDocument.close();

  await waitForImages(
    printDocument
  );

  await new Promise(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });

  // Every student's report must occupy an even number
  // of A4 printed pages so the next student begins on
  // the front side when printing double-sided.
  //
  // Reserve slightly less than the full printable page height
  // to account for print pagination rounding.
  const pageContentMm = 265;
  const pixelsPerMm = 96 / 25.4;
  const pageHeightPx = pageContentMm * pixelsPerMm;

  const reports = [
    ...printDocument.querySelectorAll('.student-report')
  ];

  for (const report of reports) {
    const height = report.getBoundingClientRect().height;

    const estimatedPages = Math.max(
      1,
      Math.ceil((height + 2) / pageHeightPx)
    );

    const evenPages =
      2 * Math.ceil(estimatedPages / 2);

    report.style.minHeight =
      (evenPages * pageContentMm) + 'mm';
  }

  await new Promise(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });

  let cleaned = false;

  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;

    window.setTimeout(
      () => frame.remove(),
      100
    );
  };

  printWindow.addEventListener(
    'afterprint',
    cleanup,
    { once: true }
  );

  const previousDocumentTitle =
    document.title;

  document.title =
    title;

  printWindow.focus();

  try {
    printWindow.print();
  } finally {
    /*
     * Keep the content-specific title long enough
     * for the browser print/save dialog to capture it,
     * then restore the application title.
     */
    window.setTimeout(
      () => {
        document.title =
          previousDocumentTitle;
      },
      1000
    );
  }

  window.setTimeout(
    cleanup,
    60000
  );
}
